import { z } from "zod";
import { transformToDate } from "../../../common";

export enum OrderEventType {
    Filled = "Filled",
    Added = "Added",
    Deleted = "Deleted",
    Updated = "Updated",
    Rejected = "Rejected",
    StatusUnknown = "StatusUnknown",
    Timeout = "Timeout",
    PendingAction = "PendingAction",
}

export interface TtOrderEvent {
  id: string;
  created_at: Date;
  updated_at: Date;

  our_id: string | null;
  dedupe_id: string;
  event_type: string | null;
  client_order_id: string | null;
  exchange_order_id: string | null;
  guid_order_id: string | null;
  site_order_key: string | null;
  parent_order_id: string | null;
  trading_account_name: string | null;
  clearing_account_name: string | null;
  order_type: string | null;
  text_a: string | null;
  text_b: string | null;
  text_c: string | null;
  text_tt: string | null;
  instrument_name: string | null;
  instrument_key: string | null;
  instrument_product: string | null;
  market_id: string | null;
  instrument_option_type: string | null;
  instrument_strike_price: string | null;
  instrument_option_scheme: string | null;
  instrument_currency_code: string | null;
  instrument_coupon_rate: string | null;
  time_in_force: string | null;
  quantity: string | null;
  order_quantity: string | null;
  order_uom_quantity: string | null;
  working_quantity: string | null;
  disclosed_quantity: string | null;
  expire_date: string | null;
  instrument_expiration_date: string | null;
  instrument_first_delivery_date: string | null;
  is_on_hold: boolean;
  is_rejected: boolean;
  is_synthetic: boolean;
  is_staged: boolean;
  order_status: string | null;
  synth_status: string | null;
  cancelled_quantity: string | null;
  filled_quantity: string | null;
  remaining_quantity: string | null;
  buy_sell_type: string | null;
  fill_type: string | null;
  avg_fill_price: string | null;
  limit_price: string | null;
  trigger_price: string | null;
  stop_price: string | null;
  message: string | null;
  reject_message: string | null;
  reject_source: string | null;
  tick_size: string | null;
  event_time: string | null;
  exchange_event_time: string | null;
  exch_transaction_time: string | null;
  leg_count: number;
  is_parent: boolean;
  is_child: boolean;
  position_effect: string | null;
  reverse_spread: string | null;
  synthetic_parent_instrument_id: string | null;

  fill_quantity: string | null;
  updated_working_quantity: string | null;
  user_name: string | null;
}

export interface TtOrderSnapshot {
  buy_sell_type: string | null;
  guid_order_id: string | null;
  exchange_order_id: string | null;
  trading_account_name: string | null;
  commodity_id: string | null;
  text_a: string | null;
  text_b: string | null;
  text_c: string | null;
  text_tt: string | null;
  snapshot_id: string | null;
  instrument_name: string | null;
  market_id: string | null;
  instrument_option_type: string | null;
  instrument_strike_price: string | null;
  time_in_force: string | null;
  quantity: string | null;
  order_quantity: string | null;
  order_uom_quantity: string | null;
  remaining_quantity: string | null;
  filled_quantity: string | null;
  working_quantity: string | null;
  expire_date: string | null;
  instrument_expiration_date: string | null;
  instrument_first_delivery_date: string | null;
  limit_price: string | null;
  trigger_price: string | null;
  stop_price: string | null;
  deleted_at: string | null;
  deleted_reason_type: string | null;
  exchange_event_time: string;
  meta_data: TtOrderMetadata | null;
  user_name: string | null | undefined;
}

export interface TtStagedEvent {
  id: string;
  created_at: string;
  snapshot_id: string | null;
  lhc_event_id: string | null;
  tt_pseudo_id: string | null;
  dedupe_id: string;
  guid_order_id: string | null;
  event_type: string | null;
  claimed_at: string | null;
  deleted_at: string | null;

  has_associated_metadata: boolean;

  order_event: TtOrderEvent | null;
  meta_data: TtOrderMetadata | null;
}

export interface TtOrderMetadataCustomerSplit {
  customer_id: string;
  split_percentage: number | string;
  Customer?: { id: string; long_name: string; }
}

export interface TtOrderMetadata {
  id: string;
  created_at: string;
  updated_at: string;

  crop_year: number | null;

  guid_order_id: string;
  link_id: string | null;
  sub_group_id: string | null;

  expected_hta: boolean;
  expected_pricing: boolean;
  expected_position_adjust: boolean;
  expected_cash: boolean;
  expected_otc: boolean;
  otc_contract_type: string | null;
  otc_contract_amendment_type: string | null;
  contract_number: string | null;
  market_zone_id: string | null;
  basis: string | null;
  delivery_terms: string | null;

  has_necessary_info: boolean;
  should_ignore_order: boolean;

  lhc_split_percentage: number | null;

  SpecifiedCustomers: TtOrderMetadataCustomerSplit[];
}

export interface UpsertOrderMetadataRequest {
  guid_order_id?: string;
  expected_hta?: boolean;
  expected_pricing?: boolean;
  expected_position_adjust?: boolean;
  expected_cash?: boolean;
  expected_otc?: boolean;

  crop_year?: number | null;
  otc_contract_type?: string;
  should_ignore_order: boolean;

  lhc_split_percentage?: string | null;
  customers?: AssociatedMetaDataCustomerSplit[];
}

export interface AssociatedMetaDataCustomerSplit {
  customer_id: string;
  split_percentage: string;
};

export interface AssociatedMetaDataCustomerUomSplit {
  customer_id: string;
  uom_quantity: string;
}

/** One linked order and the instrument-based subgroup it belongs to. */
export interface LinkedOrderId {
  guid_order_id: string;
  sub_group_id: string;
}

export interface UpsertOrderMetadataV2Request {
  guid_order_id: string;
  sub_group_id?: string;
  expected_hta?: boolean;
  expected_pricing?: boolean;
  expected_position_adjust?: boolean;
  expected_cash?: boolean;
  expected_otc?: boolean;

  crop_year?: number | null;
  otc_contract_type?: string | null;
  otc_contract_amendment_type?: string | null;
  contract_number?: string | null;
  market_zone_id?: string | null;
  basis?: string | null;
  delivery_terms?: string | null;
  should_ignore_order: boolean;

  lhc_uom_quantity?: string | null;
  customers?: AssociatedMetaDataCustomerUomSplit[];
  linked_order_ids?: LinkedOrderId[];
}

export const TTOrdersQueryValidation = z.object({
  event_types: z.array(z.nativeEnum(OrderEventType)).optional(),
  start_date: z.string().optional().transform(transformToDate({ throwOnInvalid: true, key: "start_date", format: "YYYY-MM-DD" })),
  end_date: z.string().optional().transform(transformToDate({ throwOnInvalid: true, key: "end_date", format: "YYYY-MM-DD" })),
});

export type TTOrdersQuery = z.infer<typeof TTOrdersQueryValidation>;
